import { useState, lazy, Suspense } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useSubscribeNewsletterMutation } from '../../services/newsletter_endpoints'
import { resetConsent } from '../../helpers/consent'

const GDPR = lazy(() => import('../shared/gdpr').then(m => ({ default: m.GDPR })))
const Terms = lazy(() => import('../shared/terms').then(m => ({ default: m.Terms })))

export const Footer = () => {
  const { t } = useTranslation()
  const [showGDPR, setShowGDPR] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [newsletterEmail, setNewsletterEmail] = useState('')
  const [subscribeNewsletter, { isLoading: subscribing }] = useSubscribeNewsletterMutation()

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault()
    if (!newsletterEmail.trim()) return
    try {
      await subscribeNewsletter(newsletterEmail.trim()).unwrap()
      toast.success(t('footer.subscribeSuccess'))
      setNewsletterEmail('')
    } catch (err) {
      toast.error(err?.data?.error ?? t('footer.subscribeError'))
    }
  }

  const SOCIAL_LINKS = [
    { href: "https://www.facebook.com/profile.php?id=61593719223382", src: "/images/facebook-logo.webp", alt: "facebook" },
    { href: "https://www.instagram.com/top_spin.ro/", src: "/images/instagram-logo.webp", alt: "instagram" },
    { href: "https://wa.me/+40750496007", src: "/images/whatsapp-logo.webp", alt: "whatsapp" },
    { href: "https://discord.gg/pQGxxxNA", src: "/images/discord-logo.webp", alt: "discord" },
  ]
  return (
    <Container fluid className="footer">
      <Row className="align-items-center">
        <Col md={4} xs={12} className="xs-text padding-1 center-content-v text-center text-md-start">
          <div className="d-flex flex-column align-items-center align-items-md-start">
            <a className="header-link margin-bottom-d5" href="/about">
              {t('footer.about')}
            </a>
            <a className="header-link margin-bottom-d5" href="/faq">
              {t('footer.faq')}
            </a>
            <a className="header-link" href="mailto:support@top-spin.ro">
              {t('footer.contact')}
            </a>
          </div>
        </Col>

        <Col md={4} xs={12} className="xs-text no-padding white text-align-center order-3 order-md-2">
          <div>{t('footer.rights')}</div>
          <div>{t('footer.companyInfo')}</div>
          <div className='d-flex justify-content-center'>
            <a target="blank" href='https://anpc.ro/' rel="noopener noreferrer" className="social-link margin-right-1">
              <img
                src='/images/anpc-sal.webp'
                loading="lazy"
                width={105}
                height={25}
                alt='ANPC'/>
            </a>
            <a target="blank" href='https://ec.europa.eu/consumers/odr/' rel="noopener noreferrer" className="social-link">
              <img
                src='/images/anpc-sol.webp'
                loading="lazy"
                width={105}
                height={25}
                alt='SOL'/>
            </a>
          </div>
          <div>
            <button type="button" className="link-button" onClick={() => setShowGDPR(true)}>
              {t('footer.privacyPolicy')}
            </button>
            {' | '}
            <button type="button" className="link-button" onClick={() => setShowTerms(true)}>
              {t('footer.terms')}
            </button>
            {' | '}
            <button type="button" className="link-button" onClick={() => resetConsent()}>
              {t('footer.cookieSettings')}
            </button>
          </div>
          <div className="social-links">
            {SOCIAL_LINKS.map(({ href, src, alt }) => (
              <a key={alt} href={href} target="_blank" rel="noopener noreferrer" className="social-link">
                <img src={src} alt={alt} className="social-logo" loading="lazy" />
              </a>
            ))}
          </div>
        </Col>

        <Col md={4} xs={12} className="xs-text padding-1 center-content-v text-center text-md-end order-2 order-md-3 mb-3 mb-md-0">
          <div className="xs-text white bold margin-bottom-d5">
            {t('footer.newsletterPrompt')}
          </div>
          <Form onSubmit={handleNewsletterSubmit}>
            <InputGroup className="mx-auto me-md-0" style={{ maxWidth: '320px' }}>
              <Form.Control
                type="email"
                placeholder={t('footer.emailPlaceholder')}
                aria-label={t('footer.emailPlaceholder')}
                value={newsletterEmail}
                required
                onChange={(e) => setNewsletterEmail(e.target.value)}
              />
              <Button type="submit" className="accent no-border bold" disabled={subscribing}>
                {t('footer.subscribe')}
              </Button>
            </InputGroup>
          </Form>
        </Col>
      </Row>

      <Modal show={showGDPR} onHide={() => setShowGDPR(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t('footer.privacyModalTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Suspense fallback={<div>{t('common.loading')}</div>}>
            <GDPR showTitle={false} />
          </Suspense>
        </Modal.Body>
        <Modal.Footer>
          <Button className="primary-gradient" onClick={() => setShowGDPR(false)}>{t('common.close')}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showTerms} onHide={() => setShowTerms(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t('footer.termsModalTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Suspense fallback={<div>{t('common.loading')}</div>}>
            <Terms showTitle={false} />
          </Suspense>
        </Modal.Body>
        <Modal.Footer>
          <Button className="primary-gradient" onClick={() => setShowTerms(false)}>{t('common.close')}</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}